import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getDashboardLayout,
  saveDashboardLayout,
  saveOrgDashboardLayout,
  resetDashboardLayout,
} from 'services/dashboardLayoutService'

const FALLBACK_WIDGETS = [
  { key: 'compliance_matrix', title: 'Compliance heatmap', description: 'Task domains by entity, colour-coded by compliance status.', defaultW: 8, defaultH: 6, minW: 4, minH: 4, altitudes: ['portfolio'], filters: ['scope', 'domain'] },
  { key: 'compliance_trend', title: 'Compliance trend', description: 'Twelve-month compliance score trend.', defaultW: 4, defaultH: 6, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'ops_insights', title: 'Operational insights', description: 'Tasks, remedials, incidents and people. Combined.', defaultW: 12, defaultH: 9, minW: 6, minH: 6, altitudes: ['portfolio'], filters: ['scope', 'assignee'] },
  { key: 'tasks_overview', title: 'Tasks', description: 'Task status, completion rate and breakdown by domain.', defaultW: 6, defaultH: 9, minW: 4, minH: 6, altitudes: ['portfolio'], filters: ['scope', 'assignee'] },
  { key: 'actions_overview', title: 'Remedials', description: 'Remedials by status and severity.', defaultW: 6, defaultH: 6, minW: 4, minH: 4, altitudes: ['portfolio'], filters: ['scope', 'assignee'] },
  { key: 'incidents_overview', title: 'Incidents', description: 'Logged incidents by severity.', defaultW: 6, defaultH: 6, minW: 4, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'people_overview', title: 'People', description: 'Workload and on-time performance by responsible person.', defaultW: 6, defaultH: 7, minW: 4, minH: 5, altitudes: ['portfolio'], filters: ['scope', 'assignee'] },
  { key: 'missing_obligations', title: 'Missing obligations', description: 'Highest-priority compliance gaps across the portfolio.', defaultW: 6, defaultH: 6, minW: 4, minH: 4, altitudes: ['portfolio'], filters: ['scope', 'domain'] },
  { key: 'buildings_at_risk', title: 'Buildings at risk', description: 'Building Passports with a low compliance score or overdue obligations.', defaultW: 6, defaultH: 7, minW: 4, minH: 5, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'contractors', title: 'Contractors', description: 'Vetting status and contractor documents expiring or expired.', defaultW: 6, defaultH: 7, minW: 4, minH: 5, altitudes: ['portfolio'], filters: [] },
  { key: 'contractor_performance', title: 'Contractor performance', description: 'How each contractor is delivering in this view: tasks assigned, completed, on-time rate, late and overdue. Scoped to the current Global / Portfolio / Estate / Building view.', defaultW: 12, defaultH: 7, minW: 6, minH: 5, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'permits', title: 'Permits to work', description: 'Open permits to work, high-risk activity and permits expiring soon.', defaultW: 6, defaultH: 7, minW: 4, minH: 5, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'recent_documents', title: 'Recent documents', description: 'The latest files uploaded across your Building Passports.', defaultW: 6, defaultH: 6, minW: 4, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'upcoming_events', title: 'Upcoming events', description: 'Documents and obligations expiring soon.', defaultW: 6, defaultH: 6, minW: 4, minH: 4, altitudes: ['portfolio'], filters: ['scope', 'date_range'] },
  { key: 'domain_activity', title: 'Domain activity', description: 'Per-domain task activity. Completed, late, overdue, completion and on-time rate.', defaultW: 6, defaultH: 7, minW: 4, minH: 5, altitudes: ['portfolio'], filters: ['scope', 'domain'] },
  { key: 'task_completion', title: 'Task completion', description: 'On-time completion rate and completed / late / overdue at a glance.', defaultW: 4, defaultH: 6, minW: 3, minH: 5, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'task_progress_mix', title: 'Task progress mix', description: 'A ring showing every task in this view split into upcoming, due soon, overdue, on hold and completed. Select a row to open that list.', defaultW: 6, defaultH: 6, minW: 4, minH: 5, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'remedial_progress_mix', title: 'Remedial progress mix', description: 'A ring showing every remedial in this view split into open, overdue and resolved. Select a row to open that list.', defaultW: 6, defaultH: 6, minW: 4, minH: 5, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'tasks_by_priority', title: 'Tasks by priority', description: 'Tasks split by priority: critical, high, medium and low.', defaultW: 4, defaultH: 5, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'compliance_gauge', title: 'Compliance score', description: 'Overall compliance score for this view as a RAG gauge.', defaultW: 3, defaultH: 6, minW: 3, minH: 5, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'ontime_gauge', title: 'On-time gauge', description: 'On-time completion rate against a 90% target.', defaultW: 3, defaultH: 6, minW: 3, minH: 5, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'obligations_status', title: 'Obligations by status', description: 'Satisfied / awaiting evidence / overdue across this view.', defaultW: 4, defaultH: 5, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'remedials_severity', title: 'Remedials by severity', description: 'Open remedials split by severity. Critical, high, medium, low.', defaultW: 4, defaultH: 5, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'incidents_severity', title: 'Incidents by severity', description: 'Logged incidents split by severity.', defaultW: 4, defaultH: 5, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'buildings_rag', title: 'Buildings by RAG', description: 'How many Building Passports are on track / attention / at risk / critical.', defaultW: 4, defaultH: 6, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'awaiting_approval', title: 'Needs attention', description: 'Tasks awaiting approval, on hold, or overdue.', defaultW: 4, defaultH: 5, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'top_performers', title: 'Top performers', description: 'People ranked by on-time completion rate.', defaultW: 4, defaultH: 6, minW: 3, minH: 5, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'overdue_ageing', title: 'Overdue ageing', description: 'Overdue tasks bucketed by how late. Under a week, 1–4 weeks, over a month.', defaultW: 4, defaultH: 5, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'unassigned_tasks', title: 'Unassigned tasks', description: 'Open tasks with nobody responsible.', defaultW: 3, defaultH: 5, minW: 2, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'ontime_trend', title: 'On-time trend', description: 'For each of the last 6 months, the share of that month’s completed tasks that were finished on or before their deadline.', defaultW: 6, defaultH: 6, minW: 4, minH: 5, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'period_delta', title: 'This month vs last', description: 'On-time, completed and due this month compared with last.', defaultW: 4, defaultH: 5, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'due_this_week', title: 'Due this week', description: 'Tasks due in the next 7 days.', defaultW: 4, defaultH: 6, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
  { key: 'most_overdue', title: 'Most overdue', description: 'The tasks furthest past their deadline.', defaultW: 4, defaultH: 6, minW: 3, minH: 4, altitudes: ['portfolio'], filters: ['scope'] },
]

const FALLBACK_LAYOUT = [
  { widgetKey: 'compliance_matrix', x: 0, y: 0, w: 8, h: 6, settings: {} },
  { widgetKey: 'compliance_trend', x: 8, y: 0, w: 4, h: 6, settings: {} },
  { widgetKey: 'tasks_overview', x: 0, y: 6, w: 8, h: 7, settings: {} },
  { widgetKey: 'tasks_by_priority', x: 8, y: 6, w: 4, h: 7, settings: {} },
  { widgetKey: 'due_this_week', x: 0, y: 13, w: 4, h: 6, settings: {} },
  { widgetKey: 'most_overdue', x: 4, y: 13, w: 4, h: 6, settings: {} },
  { widgetKey: 'overdue_ageing', x: 8, y: 13, w: 4, h: 6, settings: {} },
  { widgetKey: 'unassigned_tasks', x: 0, y: 19, w: 4, h: 5, settings: {} },
  { widgetKey: 'awaiting_approval', x: 4, y: 19, w: 4, h: 5, settings: {} },
  { widgetKey: 'period_delta', x: 8, y: 19, w: 4, h: 5, settings: {} },
  { widgetKey: 'ontime_trend', x: 0, y: 24, w: 6, h: 7, settings: {} },
  { widgetKey: 'domain_activity', x: 6, y: 24, w: 6, h: 7, settings: {} },
  { widgetKey: 'people_overview', x: 0, y: 31, w: 6, h: 7, settings: {} },
  { widgetKey: 'top_performers', x: 6, y: 31, w: 6, h: 7, settings: {} },
  { widgetKey: 'actions_overview', x: 0, y: 38, w: 6, h: 6, settings: {} },
  { widgetKey: 'incidents_overview', x: 6, y: 38, w: 6, h: 6, settings: {} },
  { widgetKey: 'buildings_at_risk', x: 0, y: 44, w: 6, h: 7, settings: {} },
  { widgetKey: 'missing_obligations', x: 6, y: 44, w: 6, h: 7, settings: {} },
  { widgetKey: 'contractors', x: 0, y: 51, w: 6, h: 7, settings: {} },
  { widgetKey: 'permits', x: 6, y: 51, w: 6, h: 7, settings: {} },
  { widgetKey: 'recent_documents', x: 0, y: 58, w: 6, h: 6, settings: {} },
  { widgetKey: 'upcoming_events', x: 6, y: 58, w: 6, h: 6, settings: {} },
]

export const useDashboardLayout = (altitude = 'portfolio') => {
  const [layout, setLayout] = useState([])
  const [widgets, setWidgets] = useState([])
  const [source, setSource] = useState('system')
  const [canEditOrgDefault, setCanEditOrgDefault] = useState(false)
  const [orgLayoutAvailable, setOrgLayoutAvailable] = useState(false)
  const [loading, setLoading] = useState(true)

  const applyData = useCallback(data => {
    if (!data) return
    if (Array.isArray(data.layout)) setLayout(data.layout)
    if (Array.isArray(data.widgets)) setWidgets(data.widgets)
    if (data.source) setSource(data.source)
    if (typeof data.canEditOrgDefault === 'boolean') {
      setCanEditOrgDefault(data.canEditOrgDefault)
    }
    setOrgLayoutAvailable(data.orgLayoutAvailable === true)
  }, [])

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await getDashboardLayout(altitude)
      applyData(data.data)
    } catch (error) {
      setLayout(FALLBACK_LAYOUT)
      setWidgets(FALLBACK_WIDGETS)
      setSource('system')
      setOrgLayoutAvailable(false)
    } finally {
      setLoading(false)
    }
  }, [altitude, applyData])

  useEffect(() => {
    reload()
  }, [reload])

  const savePersonal = useCallback(async () => {
    try {
      const { data } = await saveDashboardLayout({ altitude, layout })
      applyData(data.data)
      toast.success('Saved your dashboard.')
      return true
    } catch (error) {
      toast.error('Could not save your dashboard.')
      return false
    }
  }, [altitude, layout, applyData])

  const saveOrg = useCallback(async () => {
    try {
      const { data } = await saveOrgDashboardLayout({ altitude, layout })
      applyData(data.data)
      toast.success("Saved your organisation's default dashboard.")
    } catch (error) {
      if (error?.response?.status === 403) {
        toast.error('You do not have permission to set the organisation default.')
      } else {
        toast.error('Could not save the organisation default.')
      }
    }
  }, [altitude, layout, applyData])

  const reset = useCallback(async () => {
    try {
      const { data } = await resetDashboardLayout(altitude)
      applyData(data.data)
      toast.success(
        data.data?.source === 'org'
          ? "Switched to your organisation's layout."
          : 'Reset to the standard layout.'
      )
    } catch (error) {
      toast.error('Could not reset your dashboard.')
    }
  }, [altitude, applyData])

  return {
    layout,
    widgets,
    source,
    canEditOrgDefault,
    orgLayoutAvailable,
    loading,
    setLayout,
    savePersonal,
    saveOrg,
    reset,
    reload,
  }
}

export default useDashboardLayout
